import type { ShellAuthUser } from './ports/ShellAuthApplicationPort.ts';
import { shellNavPathname } from './shellNavRoutes.ts';

/** Permission for the delivery points module (bonus handouts). */
export const SHELL_PERMISSION_PUNTOS_ENTREGA = 'app.puntos_entrega';

/** Permission for the bonus redemption module. */
export const SHELL_PERMISSION_REDENCION_BONO = 'app.redencion_bono';

/** Permission for Sync / local storage diagnostics. */
export const SHELL_PERMISSION_SYNC = 'app.sync';

/** Permission for persisted telemetry inspection (admin-only in offline RBAC seeds). */
export const SHELL_PERMISSION_OBSERVABILIDAD = 'app.observabilidad';

const PERMS_PUNTOS_Y_SYNC = [SHELL_PERMISSION_PUNTOS_ENTREGA, SHELL_PERMISSION_SYNC] as const satisfies readonly string[];
const PERMS_REDENCION_Y_SYNC = [SHELL_PERMISSION_REDENCION_BONO, SHELL_PERMISSION_SYNC] as const satisfies readonly string[];
const PERMS_ADMIN_FULL = [
  SHELL_PERMISSION_PUNTOS_ENTREGA,
  SHELL_PERMISSION_REDENCION_BONO,
  SHELL_PERMISSION_SYNC,
  SHELL_PERMISSION_OBSERVABILIDAD,
] as const satisfies readonly string[];

/**
 * Offline SQLite `roles.public_id` from seed-default.sql — keep in sync when seed role rows change (`public_id` is 8 hex).
 */
export const SEED_ROLE_PUBLIC_ID_ENTREGA = '63fad24e';
export const SEED_ROLE_PUBLIC_ID_REDENCION = '8541a920';
export const SEED_ROLE_PUBLIC_ID_ADMIN = 'fd2fb49e';

/**
 * Map `ShellAuthUser.role` to module permissions (`roles.public_id`: legacy slugs, short aliases, seeded `public_id`).
 * When `permissions` is non-empty (`permisos.codigo`), it wins and `role` augments Sync / observability module only.
 */
const ROLE_TO_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  rol_entrega_bonos: PERMS_PUNTOS_Y_SYNC,
  [SEED_ROLE_PUBLIC_ID_ENTREGA]: PERMS_PUNTOS_Y_SYNC,
  entrega_bonos: PERMS_PUNTOS_Y_SYNC,
  rol_redencion_bono: PERMS_REDENCION_Y_SYNC,
  [SEED_ROLE_PUBLIC_ID_REDENCION]: PERMS_REDENCION_Y_SYNC,
  redencion_bono: PERMS_REDENCION_Y_SYNC,
  rol_admin: PERMS_ADMIN_FULL,
  [SEED_ROLE_PUBLIC_ID_ADMIN]: PERMS_ADMIN_FULL,
  admin: PERMS_ADMIN_FULL,
  administrador: PERMS_ADMIN_FULL,
};

export function resolveShellPermissions(user: ShellAuthUser | null): ReadonlySet<string> {
  if (!user) return new Set();

  const perms = new Set<string>();

  if (user.permissions && user.permissions.length > 0) {
    for (const p of user.permissions) perms.add(p);
    const role = user.role?.trim();
    if (role) {
      const fromRole = ROLE_TO_PERMISSIONS[role] ?? ROLE_TO_PERMISSIONS[role.toLowerCase()];
      if (fromRole?.includes(SHELL_PERMISSION_SYNC)) perms.add(SHELL_PERMISSION_SYNC);
      if (fromRole?.includes(SHELL_PERMISSION_OBSERVABILIDAD)) perms.add(SHELL_PERMISSION_OBSERVABILIDAD);
    }
    return perms;
  }

  const role = user.role?.trim();
  if (!role) return perms;

  const fromRole = ROLE_TO_PERMISSIONS[role] ?? ROLE_TO_PERMISSIONS[role.toLowerCase()];
  if (fromRole) {
    for (const p of fromRole) perms.add(p);
  }

  return perms;
}

/** Permission required to show a shell nav item, or `undefined` if any authenticated session suffices. */
export function shellNavPathPermission(path: string): string | undefined {
  const pathname = shellNavPathname(path);
  if (pathname === '/puntos-entrega') return SHELL_PERMISSION_PUNTOS_ENTREGA;
  if (pathname === '/redencion-bono') return SHELL_PERMISSION_REDENCION_BONO;
  if (pathname === '/sync') return SHELL_PERMISSION_SYNC;
  if (pathname === '/observabilidad') return SHELL_PERMISSION_OBSERVABILIDAD;
  return undefined;
}
